import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar';
import AdminLayout from '../../components/AdminLayout';

const s = {
  header: { marginBottom: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontFamily: "'Inter', sans-serif", fontSize: '34px', fontWeight: 700, color: 'var(--text)' },
  titleSpan: { fontStyle: 'italic', color: 'var(--accent)' },
  headerRight: { display: 'flex', gap: '10px', alignItems: 'center' },
  backBtn: { padding: '10px 20px', background: 'transparent', border: '1px solid var(--divider)', borderRadius: 'var(--r-btn)', fontSize: '12px', color: 'var(--text-2)', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' },
  dispBtn: { padding: '10px 20px', background: 'var(--text)', color: '#FAF7F2', border: 'none', borderRadius: 'var(--r-btn)', fontSize: '12px', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 },
  empty: { padding: '60px 40px', textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--divider)', borderRadius: 'var(--r-card)', color: 'var(--text-2)', fontSize: '14px', boxShadow: 'var(--sh-card)' },
  card: { background: 'var(--surface)', border: '1px solid var(--divider)', borderRadius: 'var(--r-card)', padding: '24px', marginBottom: '12px', boxShadow: 'var(--sh-card)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  cardTitle: { fontSize: '16px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' },
  cardMeta: { fontSize: '13px', color: 'var(--text-2)' },
  badge: { padding: '4px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' },
  badgePendente: { background: 'rgba(200,146,42,0.1)', color: 'var(--accent)' },
  badgeConfirmado: { background: 'rgba(76,175,80,0.1)', color: '#2E7D32' },
  badgeCancelado: { background: 'rgba(229,57,53,0.1)', color: '#E53935' },
  detail: { fontSize: '13px', color: 'var(--text-3)', lineHeight: 1.6, background: 'var(--bg)', padding: '12px', borderRadius: 'var(--r-btn)', marginBottom: '16px' },
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  btnConfirm: { padding: '9px 18px', background: 'var(--text)', color: '#FAF7F2', border: 'none', borderRadius: 'var(--r-btn)', fontSize: '12px', fontWeight: 600, letterSpacing: '0.06em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
  btnCancel: { padding: '9px 18px', background: 'transparent', border: '1px solid var(--divider)', color: 'var(--text-2)', borderRadius: 'var(--r-btn)', fontSize: '12px', cursor: 'pointer' },
  calStatus: { fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' },
  calStatusOk: { color: '#2E7D32', fontWeight: 600 },
  calStatusErr: { color: '#E53935' },
  sectionLabel: { fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '16px', marginTop: '32px' },
  authBanner: { padding: '14px 18px', background: 'rgba(200,146,42,0.06)', border: '1px solid rgba(200,146,42,0.2)', borderRadius: 'var(--r-btn)', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' },
  authText: { fontSize: '13px', color: 'var(--text)' },
  authBtn: { padding: '8px 18px', background: 'var(--accent)', color: 'var(--text)', border: 'none', borderRadius: 'var(--r-btn)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  tipoBadge: { padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 600, background: 'rgba(137,91,64,0.08)', color: 'var(--accent)', marginTop: '4px', display: 'inline-block' },
};

const TIPOS_CONSULTA = {
  avaliacao: 'Avaliação física',
  revisao_treino: 'Consulta Personal',
  revisao_nutricao: 'Consulta Nutrição',
};

export default function Agendamentos() {
  const { agendamentos, atualizarStatusAgendamento } = useData();
  const navigate = useNavigate();
  const { criarEvento, loading: calLoading, isAuthorized } = useGoogleCalendar();
  const [calResults, setCalResults] = useState({});

  const pendentes = agendamentos.filter(a => a.status === 'pendente');
  const outros = agendamentos.filter(a => a.status !== 'pendente');

  const badgeStyle = (status) => ({
    ...s.badge,
    ...(status === 'pendente' ? s.badgePendente : ['confirmado','realizado'].includes(status) ? s.badgeConfirmado : s.badgeCancelado)
  });

  async function confirmar(ag) {
    atualizarStatusAgendamento(ag.id, 'confirmado');
    try {
      const result = await criarEvento(ag);
      setCalResults(r => ({ ...r, [ag.id]: { ok: true, link: result.eventLink } }));
    } catch (err) {
      setCalResults(r => ({ ...r, [ag.id]: { ok: false, error: err.message } }));
    }
  }

  const CardAgendamento = ({ ag }) => {
    const calResult = calResults[ag.id];
    return (
      <div style={s.card}>
        <div style={s.cardHeader}>
          <div>
            <div style={s.cardTitle}>{ag.nome}</div>
            <div style={s.cardMeta}>{ag.email}</div>
            <div style={s.tipoBadge}>{TIPOS_CONSULTA[ag.tipo] || TIPOS_CONSULTA.avaliacao}</div>
          </div>
          <div style={badgeStyle(ag.status)}>{ag.status}</div>
        </div>
        {ag.alunaId && <button style={{ ...s.btnCancel, marginBottom:'12px' }} onClick={() => navigate(`/admin/alunas/${ag.alunaId}`)}>Abrir perfil da aluna →</button>}
        <div style={s.detail}>
          <strong>Data:</strong> {new Date(ag.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })} às {ag.horario}<br />
          <strong>Modalidade:</strong> {ag.modalidade === 'presencial' ? 'Presencial (Boca Raton)' : 'Online'}<br />
          {ag.obs && <><strong>Obs:</strong> {ag.obs}</>}
        </div>
        {ag.status === 'pendente' && (
          <div style={s.actions}>
            <button
              style={s.btnConfirm}
              onClick={() => confirmar(ag)}
              disabled={calLoading}
            >
              {calLoading ? '⏳' : '📅'} Confirmar + Criar no Calendar
            </button>
            <button style={s.btnCancel} onClick={() => atualizarStatusAgendamento(ag.id, 'cancelado')}>Cancelar</button>
          </div>
        )}
        {ag.status === 'confirmado' && (
          <div style={s.actions}>
            <button style={s.btnConfirm} onClick={() => atualizarStatusAgendamento(ag.id, 'realizado')}>✓ Marcar como realizado</button>
            <button style={s.btnCancel} onClick={() => atualizarStatusAgendamento(ag.id, 'faltou')}>Registrar falta</button>
            <button style={s.btnCancel} onClick={() => atualizarStatusAgendamento(ag.id, 'cancelado')}>Cancelar</button>
          </div>
        )}
        {ag.status === 'realizado' && ag.alunaId && (
          <div style={s.actions}>
            <button style={s.btnConfirm} onClick={() => navigate(`/admin/evolucao?aluna=${ag.alunaId}`)}>Registrar avaliação</button>
          </div>
        )}
        {calResult && (
          <div style={{ ...s.calStatus, marginTop: '10px', ...(calResult.ok ? s.calStatusOk : s.calStatusErr) }}>
            {calResult.ok ? (
              <>✓ Evento criado no Google Calendar — <a href={calResult.link} target="_blank" rel="noreferrer" style={{ color: '#2E7D32' }}>Ver evento</a></>
            ) : (
              <>✗ Erro ao criar evento: {calResult.error}</>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div style={s.header}>
        <h1 style={s.title}>Agendamentos <span style={s.titleSpan}>de Avaliação</span></h1>
        <div style={s.headerRight}>
          <button style={s.dispBtn} onClick={() => navigate('/admin/disponibilidade')}>🗓 Configurar disponibilidade</button>
          <button style={s.backBtn} onClick={() => navigate('/admin')}>← Voltar</button>
        </div>
      </div>

      {!isAuthorized && (
        <div style={s.authBanner}>
          <div style={s.authText}>
            🗓 Conecte o Google Calendar para criar eventos automaticamente ao confirmar agendamentos.
          </div>
          <button style={s.authBtn} onClick={async () => {
            try { await criarEvento({ nome: 'teste', email: 'teste@teste.com', data: '2026-01-01', horario: '09:00', modalidade: 'online' }); } catch {}
          }}>
            Conectar Google Calendar
          </button>
        </div>
      )}

      {isAuthorized && (
        <div style={{ padding: '10px 14px', background: 'rgba(76,175,80,0.07)', border: '1px solid rgba(76,175,80,0.2)', borderRadius: 'var(--r-btn)', marginBottom: '20px', fontSize: '13px', color: '#2E7D32' }}>
          ✓ Google Calendar conectado — eventos serão criados automaticamente ao confirmar.
        </div>
      )}

      {agendamentos.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📅</div>
          Nenhum agendamento ainda.
        </div>
      ) : (
        <>
          {pendentes.length > 0 && (
            <>
              <div style={s.sectionLabel}>Pendentes ({pendentes.length})</div>
              {pendentes.map(ag => <CardAgendamento key={ag.id} ag={ag} />)}
            </>
          )}
          {outros.length > 0 && (
            <>
              <div style={s.sectionLabel}>Histórico</div>
              {outros.map(ag => <CardAgendamento key={ag.id} ag={ag} />)}
            </>
          )}
        </>
      )}
    </AdminLayout>
  );
}
